import React from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { edgeOnboardApi, productsApi } from '../../api/services';
import type { EdgeOnboardGoLiveCounts, EdgeOnboardSubmission, Product } from '../../api/config';
import { extractApiError } from '../../utils/apiErrors';
import { ProductForm } from '../../components/products/ProductForm';
import {
  EoChip,
  EoEmpty,
  EoIcon,
  EoPage,
  formatEoDate,
  formatEoDateTime,
  formatEoTimeAgo,
  GoLiveStateChip,
  ReviewStateChip,
  SlipStateChip,
  SubmissionReviewChip,
  useEoSnackbar,
} from './eoShared';

const EMPTY_COUNTS: EdgeOnboardGoLiveCounts = {
  submissions: 0,
  tag_slips_total: 0,
  tag_slips_available: 0,
  tag_slips_pending: 0,
  tag_slips_assigned: 0,
};

// Detail view for a single go-live: header identity, the customer's product
// submissions, the tag slips placed at the location, and the ops actions
// (review, complete, reopen, delete). Audience is inside sales / ops, so copy
// stays plain — no engineering jargon.
export const GoLiveDetailPage: React.FC = () => {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError, snackbarElement } = useEoSnackbar();

  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [actionsAnchor, setActionsAnchor] = React.useState<null | HTMLElement>(null);

  const detailKey = React.useMemo(() => ['edge-onboard', 'go-lives', id] as const, [id]);
  const submissionsKey = React.useMemo(
    () => ['edge-onboard', 'go-lives', id, 'submissions'] as const,
    [id],
  );
  const tagSlipsKey = React.useMemo(
    () => ['edge-onboard', 'go-lives', id, 'tag-slips'] as const,
    [id],
  );

  const detailQuery = useQuery({
    queryKey: detailKey,
    queryFn: () => edgeOnboardApi.goLives.getOne(id),
  });
  const submissionsQuery = useQuery({
    queryKey: submissionsKey,
    queryFn: () => edgeOnboardApi.submissions.getAll(id),
  });
  const tagSlipsQuery = useQuery({
    queryKey: tagSlipsKey,
    queryFn: () => edgeOnboardApi.tagSlips.getAll(id),
  });

  const invalidateAll = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: detailKey });
    void queryClient.invalidateQueries({ queryKey: submissionsKey });
    void queryClient.invalidateQueries({ queryKey: tagSlipsKey });
  }, [queryClient, detailKey, submissionsKey, tagSlipsKey]);

  const unreviewMutation = useMutation({
    mutationFn: (submissionId: number) => edgeOnboardApi.submissions.unreview(id, submissionId),
    onSuccess: invalidateAll,
    onError: err => showError(extractApiError(err, 'Could not un-review this submission')),
  });

  // Assigning a product is the usual review path, but it isn't always
  // available (un-assign is refused once sensors report; assign skips the
  // auto-review while slips are Pending) — a manual checkoff keeps the
  // go-live's review track from stranding at "Under Review".
  const reviewMutation = useMutation({
    mutationFn: (submissionId: number) => edgeOnboardApi.submissions.review(id, submissionId),
    onSuccess: invalidateAll,
    onError: err => showError(extractApiError(err, 'Could not mark this submission reviewed')),
  });

  const completeMutation = useMutation({
    mutationFn: () => edgeOnboardApi.goLives.complete(id),
    onSuccess: () => {
      invalidateAll();
      showSuccess('Go-live marked complete');
    },
    onError: err => showError(extractApiError(err, 'Could not complete this go-live')),
  });
  const reopenMutation = useMutation({
    mutationFn: () => edgeOnboardApi.goLives.reopen(id),
    onSuccess: () => {
      invalidateAll();
      showSuccess('Go-live reopened');
    },
    onError: err => showError(extractApiError(err, 'Could not reopen this go-live')),
  });
  const deleteMutation = useMutation({
    mutationFn: () => edgeOnboardApi.goLives.delete(id),
    onSuccess: () => {
      setDeleteOpen(false);
      navigate('/onboarding/go_lives');
    },
    onError: err => showError(extractApiError(err, 'Could not delete this go-live')),
  });

  // Submission currently in the Assign-product dialog (null = closed).
  const [assignTarget, setAssignTarget] = React.useState<EdgeOnboardSubmission | null>(null);
  // Submission whose product assignment is pending un-assign confirmation.
  const [unassignTarget, setUnassignTarget] = React.useState<EdgeOnboardSubmission | null>(null);
  // Full-size photo lightbox (null = closed).
  const [lightboxUrl, setLightboxUrl] = React.useState<string | null>(null);

  const unassignMutation = useMutation({
    mutationFn: (submissionId: number) => edgeOnboardApi.submissions.unassignProduct(id, submissionId),
    onSuccess: () => {
      setUnassignTarget(null);
      showSuccess('Product un-assigned — the tags are back to unassigned');
      invalidateAll();
    },
    onError: err => {
      setUnassignTarget(null);
      showError(extractApiError(err, 'Could not un-assign the product'));
    },
  });

  const detail = detailQuery.data;
  const submissions = submissionsQuery.data ?? [];
  const slips = tagSlipsQuery.data ?? [];
  const counts = detail?.counts ?? EMPTY_COUNTS;

  if (detailQuery.isLoading) {
    return (
      <EoPage>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </EoPage>
    );
  }

  if (detailQuery.error || !detail) {
    return (
      <EoPage>
        <Alert severity="error" sx={{ mb: 2 }}>
          {extractApiError(detailQuery.error, 'Go-live not found')}
        </Alert>
        <RouterLink className="edge-link" to="/onboarding/go_lives">
          Back to Go-Lives
        </RouterLink>
      </EoPage>
    );
  }

  const locationName = detail.location?.name ?? `Location #${detail.location_id}`;
  const awaitingReview = submissions.filter(s => s.review_state === 'Not Reviewed').length;
  const pending = counts.tag_slips_pending;
  const total = counts.tag_slips_total;
  const assigned = counts.tag_slips_assigned;
  const completeDisabled =
    detail.state === 'Complete' || pending > 0 || assigned === 0 || completeMutation.isPending;
  // One plain-language line explaining why Complete is blocked (menu tooltip
  // + header callout). Null when completion is available or already done.
  const completeBlockReason =
    detail.state === 'Complete'
      ? null
      : total === 0
        ? 'No tag slips loaded yet — completion is blocked until tags are placed.'
        : pending > 0
          ? `${pending} tag slip${pending === 1 ? '' : 's'} still Pending — the customer confirms or cancels them from their phone before this can be completed.`
          : assigned === 0
            ? "No tags placed yet — the customer hasn't tagged any products, so there's nothing to complete."
            : null;

  const reviewedByEmails = Array.from(
    new Set(submissions.map(s => s.reviewed_by_email).filter((e): e is string => Boolean(e))),
  );

  const handleCopyMacs = () => {
    if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
      showError('Copying is not available in this browser.');
      return;
    }
    const macs = slips.map(s => s.mac_address).join('\n');
    void navigator.clipboard
      .writeText(macs)
      .then(() =>
        showSuccess(`Copied ${slips.length} MAC address${slips.length === 1 ? '' : 'es'}`),
      )
      .catch(() => showError('Could not copy MAC addresses.'));
  };

  const handleDownloadCsv = () => {
    void edgeOnboardApi.tagSlips
      .downloadMacsCsv(id)
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `go_live_${id}_macs.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(err => showError(extractApiError(err, 'Could not download the CSV')));
  };

  return (
    <EoPage>
      {/* Breadcrumb */}
      <div className="eo-crumbs">
        <RouterLink className="edge-link" to="/onboarding/go_lives">
          Go-Lives
        </RouterLink>
        <EoIcon name="chevron_right" />
        <span>{locationName}</span>
      </div>

      {/* Header identity card */}
      <div className="ea-paper" style={{ padding: '22px 24px', marginBottom: 20 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h1 className="eo-title" style={{ margin: 0 }}>
                {locationName}
              </h1>
              {detail.location?.hexahq_account_id != null && (
                <EoChip tone="primary" icon="business">
                  HQ #{detail.location.hexahq_account_id}
                </EoChip>
              )}
            </div>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}
              className="muted"
            >
              <EoIcon name="person" />
              <span>{detail.customer_user?.full_name ?? '—'}</span>
              {(detail.customer_user?.email || detail.customer_user?.username) && (
                <span className="muted mono">· {detail.customer_user.email ?? detail.customer_user.username}</span>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
              {/* Labeled so two "Complete" chips can't be mistaken for each other. */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <span className="eo-audit-label" style={{ marginBottom: 0 }}>Tagging</span>
                <GoLiveStateChip state={detail.state} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <span className="eo-audit-label" style={{ marginBottom: 0 }}>Review</span>
                <ReviewStateChip state={detail.review_state} />
              </div>
              <IconButton
                aria-label="Go-live actions"
                size="small"
                onClick={e => setActionsAnchor(e.currentTarget)}
                sx={{ mt: 0.25 }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={actionsAnchor}
                open={Boolean(actionsAnchor)}
                onClose={() => setActionsAnchor(null)}
              >
                {/* No Tooltip wrapper here: a wrapping <span> breaks MenuList
                    keyboard focus, and the block reason is already visible in
                    the header callout. */}
                <MenuItem
                  disabled={completeDisabled}
                  onClick={() => {
                    setActionsAnchor(null);
                    completeMutation.mutate();
                  }}
                >
                  Complete go-live
                </MenuItem>
                {detail.state === 'Complete' && (
                  <MenuItem
                    disabled={reopenMutation.isPending}
                    onClick={() => {
                      setActionsAnchor(null);
                      reopenMutation.mutate();
                    }}
                  >
                    Reopen go-live
                  </MenuItem>
                )}
                <MenuItem
                  sx={{ color: 'var(--edge-error)' }}
                  onClick={() => {
                    setActionsAnchor(null);
                    setDeleteOpen(true);
                  }}
                >
                  Delete go-live
                </MenuItem>
              </Menu>
            </div>
            <div className="muted" style={{ fontSize: 12 }}>
              Created {formatEoDateTime(detail.created_at)} · updated{' '}
              {formatEoDateTime(detail.updated_at)}
              {reviewedByEmails.length > 0 && (
                <> · reviewed by <span className="mono">{reviewedByEmails.join(', ')}</span></>
              )}
            </div>
          </div>
        </div>
        {completeBlockReason && detail.state !== 'Complete' && (
          <div className="eo-callout warning" style={{ marginTop: 14 }}>
            <EoIcon name="warning_amber" />
            <span>{completeBlockReason}</span>
          </div>
        )}
      </div>

      {/* Full-width body — actions live in the header menu, audit in the
          header meta line, so the tables get the whole viewport. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Submissions */}
          <div className="ea-paper" style={{ overflow: 'hidden' }}>
            <div className="eo-section-head">
              <div className="eo-section-title">Submissions</div>
              <div className="muted" style={{ fontSize: 12.5 }}>
                {submissions.length} total · {awaitingReview} awaiting review
              </div>
            </div>
            {submissionsQuery.isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : submissionsQuery.error ? (
              // A failed load must not read as "the customer did nothing".
              <Alert severity="error" sx={{ m: 2 }}>
                {extractApiError(submissionsQuery.error, "Couldn't load submissions")}
              </Alert>
            ) : submissions.length === 0 ? (
              <EoEmpty
                icon="inventory_2"
                message="No submissions yet — the customer adds products from their phone."
              />
            ) : (
              <div className="eo-table-wrap">
              <table className="eo-table">
                <thead>
                  <tr>
                    <th style={{ width: 56 }} aria-label="Photo" />
                    <th>Submission</th>
                    <th>Product</th>
                    <th style={{ textAlign: 'center' }}>Qty</th>
                    <th style={{ textAlign: 'center' }} title="Restocking max — blank means no restocking">
                      Max
                    </th>
                    <th style={{ textAlign: 'center', width: 56 }}>Notes</th>
                    <th>Review</th>
                    <th style={{ textAlign: 'right' }} aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {submissions.map(s => {
                    const rowBusy =
                      (unreviewMutation.isPending && unreviewMutation.variables === s.id) ||
                      (reviewMutation.isPending && reviewMutation.variables === s.id);
                    const hasPendingSlips = slips.some(
                      sl => sl.submission_id === s.id && sl.state === 'Pending',
                    );
                    return (
                      <tr key={s.id}>
                        <td>
                          {s.photo_url ? (
                            <button
                              type="button"
                              onClick={() => setLightboxUrl(s.photo_url)}
                              title="View full-size photo"
                              style={{
                                display: 'block',
                                width: 40,
                                height: 40,
                                padding: 0,
                                border: 0,
                                background: 'none',
                                cursor: 'zoom-in',
                              }}
                            >
                              <img
                                src={s.photo_url}
                                alt={`Product the customer submitted on submission ${s.id}`}
                                style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 6,
                                  objectFit: 'cover',
                                  display: 'block',
                                }}
                              />
                            </button>
                          ) : (
                            <div
                              title="No photo on this submission"
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: 6,
                                background: '#dde7ee',
                                display: 'grid',
                                placeItems: 'center',
                              }}
                            >
                              <EoIcon name="image" />
                            </div>
                          )}
                        </td>
                        <td>
                          <div>Submission #{s.id}</div>
                          <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                            {formatEoDate(s.created_at)}
                          </div>
                        </td>
                        <td>
                          {s.assigned_product ? (
                            <div>
                              <div>
                                <span className="mono" style={{ fontSize: 12 }}>{s.assigned_product.sku}</span>{' '}
                                <button
                                  className="edge-link"
                                  type="button"
                                  style={{ fontSize: 11 }}
                                  disabled={unassignMutation.isPending}
                                  onClick={() => setUnassignTarget(s)}
                                >
                                  Un-assign
                                </button>
                              </div>
                              {s.assigned_product.name && (
                                <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                                  {s.assigned_product.name}
                                </div>
                              )}
                            </div>
                          ) : slips.some(sl => sl.submission_id === s.id && sl.state === 'Assigned') ? (
                            <button className="ea-btn outlined small" onClick={() => setAssignTarget(s)}>
                              Assign product
                            </button>
                          ) : (
                            <span className="muted" title="Available after the customer confirms tagging">
                              —
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>{s.quantity}</td>
                        <td style={{ textAlign: 'center' }}>
                          {s.restocking ? s.max_quantity : <span className="muted">—</span>}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {s.notes ? (
                            <Tooltip title={s.notes} arrow>
                              <span style={{ display: 'inline-flex', cursor: 'help', color: 'var(--edge-primary)' }}>
                                <EoIcon name="sticky_note_2" />
                              </span>
                            </Tooltip>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td>
                          <SubmissionReviewChip state={s.review_state} />
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {/* Assigning a product IS the usual review — Un-review is the
                              undo, and Mark reviewed covers the paths where assign can't
                              (see reviewMutation comment). */}
                          {s.review_state === 'Reviewed' ? (
                            <button
                              className="ea-btn text small"
                              disabled={rowBusy}
                              onClick={() => unreviewMutation.mutate(s.id)}
                            >
                              Un-review
                            </button>
                          ) : (
                            <Tooltip
                              title={hasPendingSlips ? 'Confirm or cancel this submission’s pending slips first.' : ''}
                              arrow
                            >
                              <span>
                                <button
                                  className="ea-btn text small"
                                  disabled={rowBusy || hasPendingSlips}
                                  onClick={() => reviewMutation.mutate(s.id)}
                                >
                                  Mark reviewed
                                </button>
                              </span>
                            </Tooltip>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>

          {/* Tag Slips */}
          <div className="ea-paper" style={{ overflow: 'hidden' }}>
            <div className="eo-section-head">
              <div className="eo-section-title">
                Tag Slips <span className="muted">· {slips.length}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="ea-btn ghost small" onClick={handleCopyMacs} disabled={slips.length === 0}>
                  <EoIcon name="content_copy" />
                  Copy all MACs
                </button>
                <button className="ea-btn ghost small" onClick={handleDownloadCsv} disabled={slips.length === 0}>
                  <EoIcon name="download" />
                  Download CSV
                </button>
              </div>
            </div>

            {tagSlipsQuery.isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : tagSlipsQuery.error ? (
              <Alert severity="error" sx={{ m: 2 }}>
                {extractApiError(tagSlipsQuery.error, "Couldn't load tag slips")}
              </Alert>
            ) : slips.length === 0 ? (
              <EoEmpty
                icon="bluetooth"
                message="No tag slips yet — load them from the Tag Slips page."
              />
            ) : (
              <>
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    padding: '12px 18px',
                    borderBottom: '1px solid var(--edge-divider)',
                    flexWrap: 'wrap',
                  }}
                >
                  <EoChip tone="success" small icon="check_circle">
                    {assigned} Assigned
                  </EoChip>
                  <EoChip tone="warning" small icon="schedule">
                    {pending} Pending
                  </EoChip>
                  <EoChip tone="default" small icon="circle">
                    {counts.tag_slips_available} Available
                  </EoChip>
                </div>
                <div className="eo-table-wrap">
                <table className="eo-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>MAC</th>
                      <th>State</th>
                      <th>Submission</th>
                      <th>Pending Since</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slips.map(slip => (
                      <tr key={slip.id}>
                        <td className="mono">{slip.slip_id}</td>
                        <td className="mono">{slip.mac_address}</td>
                        <td>
                          <SlipStateChip state={slip.state} small />
                        </td>
                        <td className="mono">
                          {slip.submission_id != null ? `Submission #${slip.submission_id}` : '—'}
                        </td>
                        <td className="muted">{formatEoTimeAgo(slip.pending_since)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                <div
                  className="muted"
                  style={{
                    padding: '11px 18px',
                    borderTop: '1px solid var(--edge-divider)',
                    textAlign: 'center',
                    fontSize: 12,
                  }}
                >
                  Slip IDs are sequential per go-live.
                </div>
              </>
            )}
          </div>
      </div>

      {/* Delete confirmation */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: 'eo-scope' }}
      >
        <DialogContent sx={{ pt: 3 }}>
          <div className="eo-section-title" style={{ marginBottom: 10 }}>
            Delete this go-live?
          </div>
          <div style={{ fontSize: 13.5, lineHeight: 1.5, color: 'var(--edge-text-secondary)' }}>
            This permanently deletes the go-live, all of its submissions, and all of its tag slips.
            This can&apos;t be undone.
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <button
            className="ea-btn text"
            onClick={() => setDeleteOpen(false)}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </button>
          <button
            className="ea-btn danger outlined"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
          >
            Delete
          </button>
        </DialogActions>
      </Dialog>

      {/* Un-assign confirm */}
      <Dialog
        open={Boolean(unassignTarget)}
        onClose={() => setUnassignTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: 'eo-scope' }}
      >
        <DialogContent>
          <div className="eo-section-title" style={{ marginBottom: 10 }}>
            Un-assign this product?
          </div>
          <div className="muted" style={{ fontSize: 13.5, lineHeight: 1.5 }}>
            This removes the Tag Assignments created from Submission #{unassignTarget?.id}. The tag
            slips and the product itself are untouched, and you can assign again. Tags that are
            already reporting data can't be un-assigned from here.
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <button
            className="ea-btn text"
            onClick={() => setUnassignTarget(null)}
            disabled={unassignMutation.isPending}
          >
            Cancel
          </button>
          <button
            className="ea-btn danger outlined"
            disabled={unassignMutation.isPending}
            onClick={() => unassignTarget && unassignMutation.mutate(unassignTarget.id)}
          >
            {unassignMutation.isPending ? 'Un-assigning…' : 'Un-assign'}
          </button>
        </DialogActions>
      </Dialog>

      <AssignProductDialog
        goLiveId={id}
        submission={assignTarget}
        onZoom={url => setLightboxUrl(url)}
        onClose={() => setAssignTarget(null)}
        onAssigned={product => {
          setAssignTarget(null);
          showSuccess(`Product assigned — ${product.sku} is now tracked at this location`);
          invalidateAll();
        }}
      />

      {/* Photo lightbox — dark backdrop, click/Esc to close */}
      <Dialog
        open={Boolean(lightboxUrl)}
        onClose={() => setLightboxUrl(null)}
        maxWidth={false}
        PaperProps={{
          sx: { background: 'transparent', boxShadow: 'none', m: 0, cursor: 'zoom-out' },
          onClick: () => setLightboxUrl(null),
        }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.88)' } } }}
      >
        {lightboxUrl && (
          <img
            src={lightboxUrl}
            alt="Customer-submitted product, full size"
            style={{ maxWidth: '92vw', maxHeight: '92vh', display: 'block', borderRadius: 8 }}
          />
        )}
      </Dialog>

      {snackbarElement}
    </EoPage>
  );
};

interface AssignProductDialogProps {
  goLiveId: number;
  /** The submission being assigned; null keeps the dialog closed. */
  submission: EdgeOnboardSubmission | null;
  onClose: () => void;
  onAssigned: (product: Product) => void;
  /** Open the parent page's photo lightbox. */
  onZoom: (url: string) => void;
}

// The onboarding → inventory bridge: pick the product the customer's photo
// shows, and every confirmed tag on the submission becomes a live Tag
// Assignment at this location.
const AssignProductDialog: React.FC<AssignProductDialogProps> = ({
  goLiveId,
  submission,
  onClose,
  onAssigned,
  onZoom,
}) => {
  const queryClient = useQueryClient();
  const [product, setProduct] = React.useState<Product | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  // Missing-product escape hatch (the slow path in the manual workflow this
  // dialog replaces). Reuses the Products page's ProductForm wholesale — one
  // implementation of product creation, opened as its own dialog layer, with
  // the created product landing selected in the picker below.
  const [productFormOpen, setProductFormOpen] = React.useState(false);
  const open = Boolean(submission);

  React.useEffect(() => {
    if (open) {
      setProduct(null);
      setError(null);
      setProductFormOpen(false);
    }
  }, [open]);

  const productsQuery = useQuery({
    queryKey: ['edge-onboard', 'products-all'],
    queryFn: () => productsApi.getAll(),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const createProductMutation = useMutation({
    mutationFn: (data: Parameters<typeof productsApi.create>[0]) => productsApi.create(data),
    onSuccess: created => {
      void queryClient.invalidateQueries({ queryKey: ['edge-onboard', 'products-all'] });
      // The Products page caches under its own keys — keep it honest too.
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      void queryClient.invalidateQueries({ queryKey: ['product_assignment_counts'] });
      setProduct(created);
      setProductFormOpen(false);
    },
  });

  const assignMutation = useMutation({
    mutationFn: ({ submissionId, sku }: { submissionId: number; sku: string }) =>
      edgeOnboardApi.submissions.assignProduct(goLiveId, submissionId, sku),
    onSuccess: () => product && onAssigned(product),
    onError: err => setError(extractApiError(err, 'Could not assign the product')),
  });

  return (
    // Dialogs portal outside .eo-scope — re-apply it for the ea-* styles.
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ className: 'eo-scope' }}>
      <DialogTitle sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600 }}>
        Assign product{submission ? ` — Submission #${submission.id}` : ''}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
          Use the photo to identify the product. Every confirmed tag on this submission becomes a
          Tag Assignment for it at this location.
        </div>
        {submission?.photo_url && (
          <button
            type="button"
            onClick={() => onZoom(submission.photo_url as string)}
            title="View full-size photo"
            style={{ padding: 0, border: 0, background: 'none', cursor: 'zoom-in', textAlign: 'left' }}
          >
            <img
              src={submission.photo_url}
              alt="Submission product"
              style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 6, display: 'block' }}
            />
          </button>
        )}
        <Autocomplete
          options={productsQuery.data ?? []}
          value={product}
          loading={productsQuery.isLoading}
          onChange={(_, value) => setProduct(value)}
          getOptionLabel={p => `${p.sku} (${p.name})`}
          isOptionEqualToValue={(a, b) => a.sku === b.sku}
          renderInput={params => (
            <TextField
              {...params}
              label="Product"
              required
              helperText={
                productsQuery.error
                  ? 'Could not load the product list — try reopening this dialog.'
                  : 'Search by SKU or name.'
              }
            />
          )}
        />
        {!productFormOpen && (
          <div className="ea-helper" style={{ margin: 0 }}>
            Product not in Edge Admin yet?{' '}
            <button
              className="edge-link"
              type="button"
              onClick={() => {
                // A failed create from a previous open must not leak its error
                // into this one.
                createProductMutation.reset();
                setProductFormOpen(true);
              }}
            >
              Add it now
            </button>
          </div>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <button className="ea-btn text" onClick={onClose} disabled={assignMutation.isPending}>
          Cancel
        </button>
        <button
          className="ea-btn contained"
          disabled={!product || !submission || assignMutation.isPending}
          onClick={() => {
            if (!product || !submission) return;
            setError(null);
            assignMutation.mutate({ submissionId: submission.id, sku: product.sku });
          }}
        >
          {assignMutation.isPending ? 'Assigning…' : 'Assign Product'}
        </button>
      </DialogActions>
      {/* The Products page's create form, stacked as its own dialog layer. */}
      <ProductForm
        open={productFormOpen}
        onClose={() => {
          createProductMutation.reset();
          setProductFormOpen(false);
        }}
        onSubmit={data => createProductMutation.mutate(data)}
        error={createProductMutation.error ? extractApiError(createProductMutation.error) : undefined}
        isPending={createProductMutation.isPending}
      />
    </Dialog>
  );
};
